import { useState, useEffect, useMemo } from 'react';
import { Modal, Form, Input, Select, message, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

const { Option } = Select;

const PaymentDetailsModal = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  initialData = null,
  isEdit = false
}) => {
  const { t } = useTranslation('operator');
  const [form] = Form.useForm();
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [formValues, setFormValues] = useState(null);

  const initialValues = useMemo(() => {
    if (initialData) {
      return {
        paymentGateway: initialData.paymentGateway,
        SX: initialData.SX,
        IPTAL_SX: initialData.IPTAL_SX,
        LISTE_SX: initialData.LISTE_SX,
        SECRET_KEY: initialData.SECRET_KEY,
      };
    }
    return null;
  }, [initialData]);

  const hasChanges = useMemo(() => {
    if (!isEdit || !initialValues || !formValues) {
      return true;
    }
    return (
      formValues.paymentGateway !== initialValues.paymentGateway ||
      formValues.SX !== initialValues.SX ||
      formValues.IPTAL_SX !== initialValues.IPTAL_SX ||
      formValues.LISTE_SX !== initialValues.LISTE_SX ||
      formValues.SECRET_KEY !== initialValues.SECRET_KEY
    );
  }, [isEdit, initialValues, formValues]);

  useEffect(() => {
    if (isOpen && initialData) {
      const values = {
        paymentGateway: initialData.paymentGateway,
        SX: initialData.SX,
        IPTAL_SX: initialData.IPTAL_SX,
        LISTE_SX: initialData.LISTE_SX,
        SECRET_KEY: initialData.SECRET_KEY,
      };
      form.setFieldsValue(values);
      setFormValues(values);
    } else if (isOpen && !initialData) {
      form.resetFields();
      setFormValues(null);
    }
  }, [isOpen, initialData, form]);

  const handleValuesChange = (changedValues, allValues) => {
    setFormValues(allValues);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setConfirmLoading(true);
      await onSubmit(values);
      form.resetFields();
      onClose();
    } catch (error) {
      if (error.errorFields) {
        return;
      }
      message.error(
        isEdit
          ? t('paymentDetailsUpdateError')
          : t('paymentDetailsCreateError')
      );
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleCancel = () => {
    // Prevent closing during loading
    if (confirmLoading || isLoading) {
      return;
    }
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={isEdit ? t('paymentDetailsUpdateTitle') : t('paymentDetailsCreateTitle')}
      open={isOpen}
      onOk={handleSubmit}
      okButtonProps={{
        disabled: confirmLoading || isLoading || (isEdit && !hasChanges),
        loading: confirmLoading || isLoading,
      }}
      onCancel={handleCancel}
      confirmLoading={confirmLoading || isLoading}
      okText={isEdit ? t('update') : t('create')}
      cancelText={t('cancel')}
      destroyOnHidden
      width={600}
      maskClosable={!confirmLoading && !isLoading}
      closable={!confirmLoading && !isLoading}
      cancelButtonProps={{
        disabled: confirmLoading || isLoading,
      }}
    >
      <Alert
        message={t('paymentDetailsSecurityMessage')}
        type="info"
        showIcon
        style={{ 
          marginBottom: 16, 
          backgroundColor: '#ffffff',
          border: '1px solid #d9d9d9'
        }}
      />
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        onValuesChange={handleValuesChange}
      >
        <Form.Item
          name="paymentGateway"
          label={t('paymentGateway')}
          rules={[
            {
              required: true,
              message: t('paymentGatewayRequired'),
            },
          ]}
        >
          <Select
            placeholder={t('paymentGatewayPlaceholder')}
            disabled={isLoading || confirmLoading}
            allowClear
          >
            <Option value="PAYNKOLAY">PAYNKOLAY</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="SX"
          label={t('SX')}
          rules={[
            {
              required: true,
              message: t('sxRequired'),
            },
          ]}
        >
          <Input
            placeholder={t('SXPlaceholder')}
            disabled={isLoading || confirmLoading}
          />
        </Form.Item>

        <Form.Item
          name="IPTAL_SX"
          label={t('IPTAL_SX')}
          rules={[
            {
              required: true,
              message: t('iptalSxRequired'),
            },
          ]}
        >
          <Input
            placeholder={t('IPTAL_SXPlaceholder')}
            disabled={isLoading || confirmLoading}
          />
        </Form.Item>

        <Form.Item
          name="LISTE_SX"
          label={t('LISTE_SX')}
          rules={[
            {
              required: true,
              message: t('listeSxRequired'),
            },
          ]}
        >
          <Input
            placeholder={t('LISTE_SXPlaceholder')}
            disabled={isLoading || confirmLoading}
          />
        </Form.Item>

        <Form.Item
          name="SECRET_KEY"
          label={t('SECRET_KEY')}
          rules={[
            {
              required: true,
              message: t('secretKeyRequired'),
            },
          ]}
        >
          <Input.Password
            placeholder={t('SECRET_KEYPlaceholder')}
            disabled={isLoading || confirmLoading}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

PaymentDetailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  initialData: PropTypes.object,
  isEdit: PropTypes.bool,
};

export default PaymentDetailsModal;
